import { PublicPlan } from '@wix/ambassador-pricing-plans-v2-plan/types';
import { planPurchaseClick, plansPageView } from '@wix/bi-logger-pricing-plans-data/v2';
import { TPA_EXPERIMENTS } from '@wix/pricing-plans-common/experiments';
import { getSetupFeeAmount, hasSetupFee } from '@wix/pricing-plans-utils';
import { ControllerFlowAPI, ControllerParams } from '@wix/yoshi-flow-editor';
import { QUERY_REQUEST_LIMIT, REQUEST_FILTER_LIMIT } from '../../constants';
import { ButtonDemo, TabState } from '../../constants/settings-events';
import { Analytics } from '../../services/analytics';
import { PlansApi } from '../../services/plans';
import { isUuid } from '../../services/uuid-compression';
import { WarmupData } from '../../services/WarmupData';
import { ModalType, noModal } from '../../types/common';
import { PlanListInteractions } from '../../types/PlanListFedops';
import { resolveLocale } from '../../utils';
import { WIDGET_TYPE } from '../../utils/bi';
import {
  BiCheckoutVersion,
  createEcomCheckout,
  fetchPlanV3,
  navigateToEcomCheckout,
  navigateToPlanCustomization,
} from '../../utils/ecom';
import { is403 } from '../../utils/errors';
import { generateUuid } from '../../utils/generate-uuid';
import { getOrderedVisiblePlans } from '../../utils/plan-visibility';
import { retry401 } from '../../utils/retry401';
import { captureViewerException } from '../../utils/viewer-errors';
import { Navigation } from '../PackagePicker/controller/Navigation';
import { SettingsReader } from '../PackagePicker/DefaultSettingsAdapter';
import { ListWidgetWithModalProps } from './Widget';

export class PlanListController {
  constructor(
    public setProps: (props: Partial<ListWidgetWithModalProps>) => void,
    protected plansApi: PlansApi,
    protected flowAPI: ControllerFlowAPI,
    protected wixCodeApi: ControllerParams['controllerConfig']['wixCodeApi'],
    protected appParams: ControllerParams['controllerConfig']['appParams'],
    protected analytics: Analytics,
    protected settings: SettingsReader,
    protected demoPlans: PublicPlan[],
    protected warmupData: WarmupData,
    protected navigation: Navigation,
    protected isOnEcom: boolean,
  ) {}

  public async initialize() {
    const { plans, plansErrorMessage } = await this.fetchAndOrderPlans();
    this.setProps({
      plans,
      plansErrorMessage,
      tabState: TabState.REGULAR,
      buttonDemo: ButtonDemo.NONE,
      selectPlan: this.onSelectPlan,
      locale: resolveLocale(this.wixCodeApi),
      hideToast: () => this.setProps({ toast: undefined }),
      modal: noModal,
    });

    if (this.flowAPI.environment.isViewer) {
      this.flowAPI.bi?.report(
        plansPageView({
          widgetType: WIDGET_TYPE.list,
          checkoutVersion: this.isOnEcom ? BiCheckoutVersion.Ecom : BiCheckoutVersion.PricingPlans,
        }),
      );
    }
    this.analytics.addProductImpression(plans);
  }

  fetchAndOrderPlans = async () => {
    const visiblePlans = this.settings.uuidAsArray('visiblePlans').filter(isUuid);
    let plans: PublicPlan[] = [];

    try {
      const exceedsFilterLimit = visiblePlans.length > REQUEST_FILTER_LIMIT;
      plans = await this.warmupData.cache('widget.plans', () =>
        retry401(this.wixCodeApi, this.flowAPI, () =>
          exceedsFilterLimit
            ? this.plansApi.query({ limit: QUERY_REQUEST_LIMIT })
            : this.plansApi.query({ include: visiblePlans }),
        ),
      );
    } catch (e) {
      captureViewerException(this.flowAPI, e, {
        additionalIgnorePredicates: [is403],
      });

      if (this.flowAPI.experiments.enabled(TPA_EXPERIMENTS.ERROR_HANDLER_TPA)) {
        const plansErrorMessage = this.flowAPI.errorHandler.getResolvedError(e).message;
        return { plans: [], plansErrorMessage };
      }
    }

    const useFixture = !plans?.length && this.wixCodeApi.window.viewMode === 'Editor';

    return {
      plans: getOrderedVisiblePlans(useFixture ? this.demoPlans : plans, this.settings),
      plansErrorMessage: undefined,
    };
  };

  private onSelectPlan = async (plan: PublicPlan) => {
    this.flowAPI.panoramaClient?.transaction(PlanListInteractions.NavigateToList).start();

    this.setProps({ selectedPlanId: plan.id });

    const checkoutFlowId = generateUuid();
    if (this.wixCodeApi.window.viewMode !== 'Site') {
      this.setProps({
        modal: { type: ModalType.PurchaseInPreview, onClose: this.closeModal },
        selectedPlanId: undefined,
      });
      this.flowAPI.panoramaClient?.transaction(PlanListInteractions.NavigateToList).finish();
      return;
    }

    this.flowAPI.bi?.report(
      planPurchaseClick({
        planGuid: plan.id ?? '',
        widgetType: 'list',
        setupFeeAmount: hasSetupFee(plan) ? Math.round(Number(getSetupFeeAmount(plan)) * 100) : undefined,
        checkoutFlowId,
        checkoutVersion: this.isOnEcom ? BiCheckoutVersion.Ecom : BiCheckoutVersion.PricingPlans,
        isCustomStartDateEnabled: Boolean(plan.allowFutureStartDate).toString(),
        isCustomCheckoutFormEnabled: Boolean(plan.formId).toString(),
      }),
    );
    this.analytics.clickProduct(plan);

    if (this.isOnEcom) {
      const navigateToCustomization = plan.formId || plan.allowFutureStartDate;
      if (navigateToCustomization) {
        navigateToPlanCustomization(this.wixCodeApi, { planId: plan.id!, integrationData: {}, checkoutFlowId });
      } else {
        const planV3 = await fetchPlanV3({ id: plan.id }, this.flowAPI.httpClient);
        try {
          const ecomCheckout = await createEcomCheckout({
            plan: planV3,
            flowAPI: this.flowAPI,
            checkoutFlowId,
          });
          await navigateToEcomCheckout({
            checkoutId: ecomCheckout.id!,
            planId: plan.id!,
            wixCodeApi: this.wixCodeApi,
            flowAPI: this.flowAPI,
            biData: {
              checkoutFlowId,
              isCustomCheckoutFormEnabled: !!plan.formId,
              isCustomStartDateEnabled: !!plan.allowFutureStartDate,
            },
          });
        } catch (error) {
          const errorMessage = this.flowAPI.errorHandler.getResolvedError(error).message;
          this.setProps({ toast: { message: errorMessage }, selectedPlanId: undefined });
          captureViewerException(this.flowAPI, error, { tags: { interaction: PlanListInteractions.NavigateToList } });
        }
      }
    } else {
      await this.navigateToCheckout(plan, checkoutFlowId);
    }
    this.flowAPI.panoramaClient?.transaction(PlanListInteractions.NavigateToList).finish();
  };

  private closeModal = () => {
    return this.setProps({ modal: noModal });
  };

  private navigateToCheckout = async (plan: PublicPlan, checkoutFlowId?: string) => {
    return this.navigation.setCurrentPath({
      name: 'checkout',
      checkoutData: { planId: plan.id!, integrationData: {}, checkoutFlowId },
    });
  };
}
